import React, { useState, useRef } from 'react';
import { X, Plus, Sparkles, Loader2, PenLine, Bot, CheckCircle2, RefreshCw } from 'lucide-react';
import { TaskPriority, TaskStatus, TaskTeam, TaskUser } from '../types';
import { detectTeam } from '../utils/detectTeam';
import { apiService } from '../../../services/api';

interface TaskData {
  title: string;
  description: string;
  assigneeId: number;
  priority: TaskPriority;
  status: TaskStatus;
  team: TaskTeam;
  dueDate: string | null;
  labels: string[];
}

interface CreateTaskModalProps {
  users: TaskUser[];
  currentUser: TaskUser;
  teams: string[];
  onClose: () => void;
  onSubmit: (data: TaskData) => void;
}

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const LABEL_SUGGESTIONS = [
  'Frontend', 'Backend', 'Bug', 'Feature', 'Finance',
  'Ops', 'HR', 'KAM', 'Urgent', 'Process',
];

// ── Mock AI parsers (replaced with real Claude API in Phase 2) ────────────────

function generateBrief(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('interview') || t.includes('hiring') || t.includes('recruit') || t.includes('candidate'))
    return 'Schedule and conduct structured interviews with all shortlisted candidates. Evaluate responses against the defined criteria and share feedback with the hiring panel. Finalise the decision with relevant stakeholders before sending out offer letters.';
  if (t.includes('p&l') || t.includes('report') || t.includes('finance') || t.includes('revenue'))
    return 'Compile the relevant financial data and cross-check figures across all locations. Ensure the numbers align with the source systems before sharing. Flag any discrepancies and provide a summary section at the top for quick executive review.';
  if (t.includes('bug') || t.includes('fix') || t.includes('error') || t.includes('issue'))
    return 'Reproduce the issue consistently and identify the root cause. Implement a targeted fix with minimal side effects. Test across all affected environments and edge cases before marking as resolved.';
  if (t.includes('payroll') || t.includes('salary') || t.includes('attendance') || t.includes('leave'))
    return 'Pull the latest data from the HR system and cross-check against attendance records. Identify any discrepancies and escalate to the relevant manager. Ensure the final figures are approved before processing.';
  if (/\bhr\b/.test(t) || t.includes('employee') || t.includes('onboarding') || t.includes('offboarding'))
    return 'Coordinate with the HR team to complete the required steps and update all relevant records. Communicate next steps clearly to the employee and relevant managers. Confirm completion in the HR system once done.';
  if (t.includes('client') || t.includes('onboard') || t.includes('kam'))
    return 'Coordinate with the relevant city ops team to complete onboarding requirements. Verify that SKU mapping, billing configuration, and facility assignments are complete. Share a confirmation summary once done.';
  if (t.includes('invoice') || t.includes('vendor') || t.includes('approval') || t.includes('payment'))
    return 'Review the pending items in the queue and verify against supporting documents. Ensure amounts match POs or contracts. Approve or flag for clarification with a comment before the deadline.';
  if (t.includes('mail') || t.includes('email') || t.includes('reply') || t.includes('respond'))
    return 'Review all pending correspondence and draft clear, professional responses. Prioritise time-sensitive threads and loop in relevant team members where needed. Mark each thread as resolved once a reply has been sent.';
  if (t.includes('social') || t.includes('media') || t.includes('content') || t.includes('post'))
    return 'Plan and draft the required content pieces based on current goals and brand guidelines. Review with the relevant stakeholder before publishing. Track engagement metrics after posting.';
  if (t.includes('transit') || t.includes('dispatch') || t.includes('plan') || t.includes('inventory'))
    return 'Review the current plan against actual inventory levels and dispatch requirements. Update the plan to reflect any changes in client demand or facility capacity. Confirm with city leads before finalising.';
  if (t.includes('ui') || t.includes('interface') || t.includes('design') || t.includes('dashboard') || t.includes('board'))
    return 'Define the scope and component breakdown before starting. Build following existing design patterns and ensure responsive behaviour across screen sizes. Test across key user flows and get a design review before marking complete.';
  if (t.includes('build') || t.includes('feature') || t.includes('implement') || t.includes('develop') || t.includes('complete'))
    return 'Define the scope and acceptance criteria clearly before starting. Build the feature following existing patterns in the codebase. Test the happy path and edge cases, then get a peer review before marking complete.';
  return 'Break this task down into clear sub-steps before starting. Identify any dependencies or blockers upfront. Communicate progress at each stage and flag risks early so the team can course-correct if needed.';
}

function parseAIInput(input: string, users: TaskUser[]): TaskData {
  const lower = input.toLowerCase();

  // Priority
  let priority: TaskPriority = 'medium';
  if (lower.includes('urgent') || lower.includes('asap') || lower.includes('critical')) priority = 'urgent';
  else if (lower.includes('high priority') || lower.includes('very important')) priority = 'high';
  else if (lower.includes('low priority') || lower.includes('when possible') || lower.includes('not urgent')) priority = 'low';

  // Due date — all days of the week supported
  const today = new Date();
  function getNextDay(dayIndex: number): string {
    const diff = ((dayIndex - today.getDay() + 7) % 7) || 7;
    const d = new Date(today);
    d.setDate(today.getDate() + diff);
    return d.toISOString().split('T')[0];
  }
  let dueDate: string | null = null;
  if (lower.includes('today'))           { dueDate = today.toISOString().split('T')[0]; }
  else if (lower.includes('tomorrow'))   { const d = new Date(today); d.setDate(d.getDate() + 1); dueDate = d.toISOString().split('T')[0]; }
  else if (/\bsunday\b/.test(lower))    { dueDate = getNextDay(0); }
  else if (/\bmonday\b/.test(lower))    { dueDate = getNextDay(1); }
  else if (/\btuesday\b/.test(lower))   { dueDate = getNextDay(2); }
  else if (/\bwednesday\b/.test(lower)) { dueDate = getNextDay(3); }
  else if (/\bthursday\b/.test(lower))  { dueDate = getNextDay(4); }
  else if (/\bfriday\b/.test(lower) || lower.includes('end of week')) { dueDate = getNextDay(5); }
  else if (/\bsaturday\b/.test(lower))  { dueDate = getNextDay(6); }
  else if (lower.includes('next week')) { const d = new Date(today); d.setDate(d.getDate() + 7); dueDate = d.toISOString().split('T')[0]; }
  else if (lower.includes('end of month') || lower.includes('month end')) {
    dueDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
  }

  // Assignee — only set if a specific person is mentioned; -1 = unassigned
  const inputWords = lower.match(/\b[a-z]+\b/g) ?? [];
  let assigneeId = -1;
  for (const user of users) {
    const firstName = user.name.trim().split(/\s+/)[0].toLowerCase();
    if (firstName.length < 3) continue;
    const isMatch = inputWords.some(word =>
      word.length >= 4 && (firstName.startsWith(word) || word.startsWith(firstName))
    );
    if (isMatch) {
      assigneeId = user.id;
      break;
    }
  }

  // Labels — detected first so team can use them as a reliable signal
  const labels: string[] = [];
  if (lower.includes('bug') || lower.includes('fix') || lower.includes('error')) labels.push('Bug');
  if (lower.includes('feature') || lower.includes('build') || lower.includes('implement')) labels.push('Feature');
  if (lower.includes('finance') || lower.includes('p&l') || lower.includes('revenue')) labels.push('Finance');
  if (lower.includes('frontend') || lower.includes('ui') || lower.includes('interface')) labels.push('Frontend');
  if (lower.includes('backend') || lower.includes('api') || lower.includes('server')) labels.push('Backend');
  if (lower.includes('ops') || lower.includes('operation') || lower.includes('shift')) labels.push('Ops');
  if (/\bhr\b/.test(lower) || lower.includes('payroll') || lower.includes('attendance') ||
      lower.includes('interview') || lower.includes('hiring') || lower.includes('recruit') ||
      lower.includes('candidate')) labels.push('HR');
  if (lower.includes('kam') || lower.includes('client') || lower.includes('onboard')) labels.push('KAM');
  if (lower.includes('urgent') || lower.includes('asap')) labels.push('Urgent');

  // Team — derive from labels first (most reliable), fall back to keyword scoring
  const team: TaskTeam = (() => {
    if (labels.includes('HR')) return 'HR';
    if (labels.includes('Finance')) return 'Finance';
    if (labels.includes('KAM')) return 'KAM';
    if (labels.includes('Ops') && !labels.includes('Frontend') && !labels.includes('Backend')) return 'Operations';
    if (labels.includes('Frontend') || labels.includes('Backend')) return 'Engineering';
    return detectTeam(input);
  })();

  // Title — strip common prefixes then remove date/priority noise
  let title = input;
  // Pattern 1: "ask/tell/assign [name(s)] to [action]" → keep [action]
  const prefixMatch = title.match(/^(ask|tell|assign|get|have)\s+(.+?)\s+to\s+/i);
  if (prefixMatch) {
    title = title.slice(prefixMatch[0].length);
  } else {
    // Pattern 2: "ask/tell [name] [action]" (no "to")
    title = title.replace(/^(ask|tell|assign|get|have)\s+\S+\s+/i, '');
    // Pattern 3: "the [subject] should/needs to/must/can/will [action]"
    const statementMatch = title.match(/^(?:the\s+)?(?:\S+\s+){1,3}(?:should|needs?\s+to|must|can|will|has?\s+to)\s+/i);
    if (statementMatch) title = title.slice(statementMatch[0].length);
    // Pattern 4: "I want/need to [action]"
    title = title.replace(/^i\s+(want|need|have)\s+to\s+/i, '');
  }
  title = title
    .replace(/\bby\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gi, '')
    .replace(/\b(this|next)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday|week)\b/gi, '')
    .replace(/\bend of (week|month)\b/gi, '')
    .replace(/\bmonth end\b/gi, '')
    .replace(/\b(today|tomorrow)\b/gi, '')
    .replace(/,?\s*(high|medium|low|urgent)\s*priority/gi, '')
    .replace(/\s*[—–-]\s*(high|medium|low|urgent)\s*priority/gi, '')
    .replace(/\b(urgent|asap|critical)\b/gi, '')
    .replace(/\s*[—–]\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!title) title = input.trim();
  title = title.charAt(0).toUpperCase() + title.slice(1);
  if (title.endsWith('.')) title = title.slice(0, -1);

  const description = generateBrief(title);

  // Status — detect explicit backlog/deprioritize intent
  let status: TaskStatus = 'todo';
  if (
    lower.includes('backlog') ||
    lower.includes('deprioritize') ||
    lower.includes('not important') ||
    lower.includes('put it aside') ||
    lower.includes('park it') ||
    lower.includes('can wait') ||
    lower.includes('not a priority')
  ) {
    status = 'backlog';
  }

  return { title, description, assigneeId, priority, status, team, dueDate, labels };
}

// ─────────────────────────────────────────────────────────────────────────────

type Tab = 'manual' | 'ai';

export const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  users,
  currentUser,
  teams,
  onClose,
  onSubmit,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('manual');

  // ── Manual tab state ──
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState<number>(currentUser.id);
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [team, setTeam] = useState<TaskTeam>('Engineering');
  const [dueDate, setDueDate] = useState('');
  const [labels, setLabels] = useState<string[]>([]);
  const [labelInput, setLabelInput] = useState('');
  const [generatingBrief, setGeneratingBrief] = useState(false);

  // ── AI tab state ──
  const [aiInput, setAiInput] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const [aiResult, setAiResult] = useState<TaskData | null>(null);

  // ── @-mention state ──
  const aiTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionState, setMentionState] = useState<{ query: string; atIndex: number } | null>(null);
  const mentionUsers = mentionState
    ? users.filter(u => {
        const q = mentionState.query;
        return !q || u.name.toLowerCase().startsWith(q) || u.name.split(' ').some(w => w.toLowerCase().startsWith(q));
      }).slice(0, 5)
    : [];

  const handleAiInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setAiInput(val);
    const cursor = e.target.selectionStart ?? val.length;
    const before = val.slice(0, cursor);
    const match = before.match(/@(\w*)$/);
    setMentionState(match ? { query: match[1].toLowerCase(), atIndex: cursor - match[0].length } : null);
  };

  const handleMentionSelect = (user: TaskUser) => {
    if (!mentionState) return;
    const before = aiInput.slice(0, mentionState.atIndex);
    const after = aiInput.slice(mentionState.atIndex + 1 + mentionState.query.length);
    const updated = `${before}${user.name} ${after}`;
    setAiInput(updated);
    setMentionState(null);
    aiTextareaRef.current?.focus();
  };

  // Manual tab helpers
  const handleGenerateBrief = async () => {
    if (!title.trim() || generatingBrief) return;
    setGeneratingBrief(true);
    await new Promise(r => setTimeout(r, 900));
    setDescription(generateBrief(title));
    setGeneratingBrief(false);
  };

  const toggleLabel = (label: string) =>
    setLabels(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);

  const addCustomLabel = () => {
    const trimmed = labelInput.trim();
    if (trimmed && !labels.includes(trimmed)) setLabels(prev => [...prev, trimmed]);
    setLabelInput('');
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit({ title: title.trim(), description: description.trim(), assigneeId, priority, status: 'todo', team, dueDate: dueDate || null, labels });
    onClose();
  };

  // AI tab helpers
  const handleAiAnalyze = async () => {
    if (!aiInput.trim() || aiParsing) return;
    setAiParsing(true);
    setAiResult(null);

    let parsed: TaskData;
    try {
      const res = await apiService.post<{
        title: string; description: string; priority: TaskPriority;
        status: TaskStatus; team: TaskTeam; due_date: string | null; labels: string[];
      }>('/tasks/ai/parse', { input: aiInput });
      const d = res.data;
      // Resolve assignee — only set if a specific person is mentioned; -1 = unassigned
      const lower = aiInput.toLowerCase();
      const inputWords = lower.match(/\b[a-z]+\b/g) ?? [];
      let assigneeId = -1;
      for (const user of users) {
        const firstName = user.name.trim().split(/\s+/)[0].toLowerCase();
        if (firstName.length < 3) continue;
        const isMatch = inputWords.some(word =>
          word.length >= 4 && (firstName.startsWith(word) || word.startsWith(firstName))
        );
        if (isMatch) {
          assigneeId = user.id;
          break;
        }
      }
      // Derive team from labels (more reliable than AI model's team field)
      const aiLabels: string[] = d.labels ?? [];
      const derivedTeam: TaskTeam = (() => {
        if (aiLabels.includes('HR')) return 'HR';
        if (aiLabels.includes('Finance')) return 'Finance';
        if (aiLabels.includes('KAM')) return 'KAM';
        if (aiLabels.includes('Ops') && !aiLabels.includes('Frontend') && !aiLabels.includes('Backend')) return 'Operations';
        if (aiLabels.includes('Frontend') || aiLabels.includes('Backend')) return 'Engineering';
        return (d.team as TaskTeam) ?? detectTeam(aiInput);
      })();
      parsed = {
        title: d.title,
        description: d.description,
        priority: d.priority,
        status: d.status ?? 'todo',
        team: derivedTeam,
        dueDate: d.due_date,
        labels: aiLabels,
        assigneeId,
      };
    } catch {
      parsed = parseAIInput(aiInput, users);
    }

    setAiResult(parsed);
    setAiParsing(false);
  };

  const handleAiSubmit = () => {
    if (!aiResult) return;
    onSubmit({
      ...aiResult,
      assigneeId: aiResult.assigneeId > 0 ? aiResult.assigneeId : -1,
    });
    onClose();
  };

  const handleAiRetry = () => setAiResult(null);

  return (
    <div className='fixed inset-0 z-50 flex flex-col items-stretch justify-end sm:items-center sm:justify-center bg-black/40 backdrop-blur-sm sm:p-4'>
      <div className='bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-lg flex flex-col max-h-[92vh] sm:max-h-[90vh]'>

        {/* Header */}
        <div className='flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100'>
          <h2 className='text-base font-semibold text-gray-900'>Create Task</h2>
          <button onClick={onClose} className='p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors'>
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className='flex border-b border-gray-100 px-4 sm:px-6'>
          <button
            onClick={() => setActiveTab('manual')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'manual'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            <PenLine size={14} />
            Manual
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === 'ai'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            <Sparkles size={14} />
            AI
          </button>
        </div>

        {/* ── MANUAL TAB ── */}
        {activeTab === 'manual' && (
          <form onSubmit={handleManualSubmit} className='flex flex-col flex-1 overflow-y-auto'>
            <div className='px-4 sm:px-6 py-4 space-y-4'>

              {/* Title + AI Brief */}
              <div>
                <div className='flex items-center justify-between mb-1.5'>
                  <label className='text-xs font-semibold text-gray-600'>
                    Title <span className='text-red-500'>*</span>
                  </label>
                  <button
                    type='button'
                    onClick={handleGenerateBrief}
                    disabled={!title.trim() || generatingBrief}
                    className='flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
                  >
                    {generatingBrief ? <Loader2 size={11} className='animate-spin' /> : <Sparkles size={11} />}
                    {generatingBrief ? 'Generating…' : 'AI Brief'}
                  </button>
                </div>
                <input
                  type='text'
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder='What needs to be done?'
                  className='w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 transition-colors placeholder-gray-400'
                  autoFocus
                />
              </div>

              {/* Description */}
              <div>
                <label className='block text-xs font-semibold text-gray-600 mb-1.5'>Description</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder='Add more details… or use AI Brief to auto-fill'
                  rows={3}
                  className='w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 transition-colors resize-none placeholder-gray-400'
                />
              </div>

              {/* Assignee + Priority */}
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <div>
                  <label className='block text-xs font-semibold text-gray-600 mb-1.5'>Assignee</label>
                  <select value={assigneeId} onChange={e => setAssigneeId(Number(e.target.value))}
                    className='w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-blue-500 bg-white transition-colors'>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className='block text-xs font-semibold text-gray-600 mb-1.5'>Priority</label>
                  <select value={priority} onChange={e => setPriority(e.target.value as TaskPriority)}
                    className='w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-blue-500 bg-white transition-colors'>
                    {PRIORITY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Team + Due date */}
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <div>
                  <label className='block text-xs font-semibold text-gray-600 mb-1.5'>Team</label>
                  <select value={team} onChange={e => setTeam(e.target.value as TaskTeam)}
                    className='w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-blue-500 bg-white transition-colors'>
                    {teams.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className='block text-xs font-semibold text-gray-600 mb-1.5'>Due Date</label>
                  <input type='date' value={dueDate} onChange={e => setDueDate(e.target.value)}
                    className='w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-50 transition-colors bg-white' />
                </div>
              </div>

              {/* Labels */}
              <div>
                <label className='block text-xs font-semibold text-gray-600 mb-1.5'>Labels</label>
                <div className='flex flex-wrap gap-1.5 mb-2'>
                  {LABEL_SUGGESTIONS.map(label => (
                    <button key={label} type='button' onClick={() => toggleLabel(label)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        labels.includes(label) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className='flex gap-2'>
                  <input type='text' value={labelInput} onChange={e => setLabelInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomLabel())}
                    placeholder='Custom label…'
                    className='flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-blue-500 placeholder-gray-400' />
                  <button type='button' onClick={addCustomLabel} className='px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors'>
                    <Plus size={14} className='text-gray-600' />
                  </button>
                </div>
              </div>
            </div>

            <div className='flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl sm:rounded-b-xl'>
              <button type='button' onClick={onClose} className='px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-medium'>
                Cancel
              </button>
              <button type='submit' disabled={!title.trim()}
                className='px-5 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'>
                Create Task
              </button>
            </div>
          </form>
        )}

        {/* ── AI TAB ── */}
        {activeTab === 'ai' && (
          <div className='flex flex-col flex-1 overflow-y-auto'>
            <div className='px-4 sm:px-6 py-5 space-y-4 flex-1'>

              {/* Prompt box */}
              {!aiResult && (
                <>
                  <div className='flex items-start gap-3 p-3.5 bg-purple-50 border border-purple-100 rounded-xl'>
                    <Bot size={16} className='text-purple-600 flex-shrink-0 mt-0.5' />
                    <p className='text-xs text-purple-800 leading-relaxed'>
                      Describe the task in plain English. Include who should do it, when it&apos;s due, priority, and any context. AI will fill everything automatically.
                    </p>
                  </div>

                  <div>
                    <label className='block text-xs font-semibold text-gray-600 mb-1.5'>
                      Describe the task
                      <span className='ml-1.5 font-normal text-gray-400'>— type @ to tag someone</span>
                    </label>
                    <div className='relative'>
                      {mentionUsers.length > 0 && (
                        <div className='absolute bottom-full left-0 right-0 mb-1 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden z-50'>
                          {mentionUsers.map(user => (
                            <button
                              key={user.id}
                              type='button'
                              onMouseDown={e => { e.preventDefault(); handleMentionSelect(user); }}
                              className='w-full flex items-center gap-2.5 px-3 py-2 hover:bg-purple-50 text-left transition-colors'
                            >
                              <div className={`w-6 h-6 rounded-full ${user.avatarColor} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}>
                                {user.initials}
                              </div>
                              <span className='text-sm text-gray-800'>{user.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <textarea
                        ref={aiTextareaRef}
                        value={aiInput}
                        onChange={handleAiInputChange}
                        onKeyDown={e => { if (e.key === 'Escape') setMentionState(null); }}
                        placeholder={`e.g. "Ask @Gouri to prepare the June P&L report by this Friday — high priority"`}
                        rows={5}
                        autoFocus
                        className='w-full text-sm border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-50 transition-colors resize-none placeholder-gray-400 leading-relaxed'
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleAiAnalyze}
                    disabled={!aiInput.trim() || aiParsing}
                    className='w-full flex items-center justify-center gap-2 py-3 bg-purple-600 text-white rounded-xl font-medium text-sm hover:bg-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
                  >
                    {aiParsing
                      ? <><Loader2 size={15} className='animate-spin' /> Analysing…</>
                      : <><Sparkles size={15} /> Analyse & Fill Fields</>}
                  </button>
                </>
              )}

              {/* AI result preview */}
              {aiResult && (
                <div className='space-y-4'>
                  <div className='flex items-center gap-2'>
                    <CheckCircle2 size={16} className='text-emerald-600' />
                    <p className='text-sm font-semibold text-gray-800'>AI filled the task — review before creating</p>
                  </div>

                  <div className='bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3'>
                    {/* Title */}
                    <div>
                      <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1'>Title</p>
                      <input
                        value={aiResult.title}
                        onChange={e => setAiResult(r => r ? { ...r, title: e.target.value } : r)}
                        className='w-full text-sm font-medium text-gray-900 border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-purple-400 bg-white'
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1'>Description</p>
                      <textarea
                        value={aiResult.description}
                        onChange={e => setAiResult(r => r ? { ...r, description: e.target.value } : r)}
                        rows={3}
                        className='w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-purple-400 resize-none bg-white'
                      />
                    </div>

                    {/* Fields grid */}
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                      <div>
                        <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1'>Assignee</p>
                        <select
                          value={aiResult.assigneeId > 0 ? aiResult.assigneeId : ''}
                          onChange={e => setAiResult(r => r ? { ...r, assigneeId: e.target.value ? Number(e.target.value) : -1 } : r)}
                          className='w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 bg-white outline-none focus:border-purple-400'
                        >
                          <option value=''>— Unassigned —</option>
                          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1'>Priority</p>
                        <select
                          value={aiResult.priority}
                          onChange={e => setAiResult(r => r ? { ...r, priority: e.target.value as TaskPriority } : r)}
                          className='w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 bg-white outline-none focus:border-purple-400'
                        >
                          {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1'>Team</p>
                        <select
                          value={aiResult.team}
                          onChange={e => setAiResult(r => r ? { ...r, team: e.target.value as TaskTeam } : r)}
                          className='w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 bg-white outline-none focus:border-purple-400'
                        >
                          {teams.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1'>Due Date</p>
                        <input
                          type='date'
                          value={aiResult.dueDate ?? ''}
                          onChange={e => setAiResult(r => r ? { ...r, dueDate: e.target.value || null } : r)}
                          className='w-full text-sm border border-gray-200 rounded-lg px-2.5 py-2 bg-white outline-none focus:border-purple-400'
                        />
                      </div>
                    </div>

                    {/* Labels */}
                    {aiResult.labels.length > 0 && (
                      <div>
                        <p className='text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5'>Labels</p>
                        <div className='flex flex-wrap gap-1.5'>
                          {aiResult.labels.map(label => (
                            <span key={label} className='text-xs px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 font-medium'>
                              {label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleAiRetry}
                    className='flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors'
                  >
                    <RefreshCw size={12} /> Try different input
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className='flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl sm:rounded-b-xl'>
              <button type='button' onClick={onClose}
                className='px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors font-medium'>
                Cancel
              </button>
              <button
                onClick={handleAiSubmit}
                disabled={!aiResult}
                className='px-5 py-2 text-sm bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
              >
                Create Task
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
